// ─── Widget Registry ─────────────────────────────────────
// Single source of truth for all dashboard widget definitions.
// Used by DashboardWidgets.tsx, settings.tsx, and the customize sheet.

export type WidgetCategory = 'personal' | 'team' | 'management' | 'content' | 'analytics' | 'social';

export interface WidgetDef {
  id: string;
  label: string;
  description: string;
  /** Permissions that grant visibility (OR logic). Empty = visible to all. */
  visibleWithPermissions: string[];
  /** System roles that see this widget enabled by default (first-time). */
  defaultForRoles: string[];
  /** Team positions that see this widget enabled by default. */
  defaultForPositions: string[];
  /** Grid column span classes */
  gridSpan: { default: string; sm?: string; lg?: string };
  /** Category for grouping in the customize sheet */
  category: WidgetCategory;
  /** Display priority (lower = rendered earlier). */
  priority: number;
  /**
   * For summary-driven widgets: returns true when the widget has no meaningful
   * data, so the dashboard can demote it below populated widgets. Only defined
   * for widgets whose emptiness is derivable from the dashboard summary (`s`)
   * or the user's tasks (`tasks`). Self-fetching widgets report emptiness at
   * runtime via `useReportTileEmpty` instead.
   */
  isEmpty?: (s: any, tasks: any[]) => boolean;
  /**
   * Pinned widgets stay in the top zone of the grid and are never demoted,
   * even when empty (e.g. company news).
   */
  pinned?: boolean;
}

// ─── Roles & Positions used in default arrays ─────────────
const MGMT_ROLES = ['superadmin', 'admin'];
const BASIC_ROLES = ['user', 'staff'];

// ─── Registry ────────────────────────────────────────────

export const WIDGET_REGISTRY: WidgetDef[] = [
  // ── Personal ──────────────────────────────────────
  {
    id: 'quick_access',
    label: 'Quick Access',
    description: 'Shortcut tiles to key pages',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12' },
    category: 'personal',
    priority: 65,
  },
  {
    id: 'my_tasks_summary',
    label: 'My Tasks',
    description: 'Overdue, due today, and in-progress task counts',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'personal',
    priority: 90,
    isEmpty: (s) => s?.myTasksSummary?.totalActive === 0,
  },
  {
    id: 'leave_balance',
    label: 'Leave Balance',
    description: 'Your remaining leave days by type',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'personal',
    priority: 92,
  },
  {
    id: 'effort_summary',
    label: 'Last 7 Days',
    description: 'Daily hours logged for the past 7 days with quick log',
    visibleWithPermissions: [],
    defaultForRoles: [...BASIC_ROLES],
    defaultForPositions: ['member', 'team_lead'],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'personal',
    priority: 35,
    // The backend always returns 7 day-slots, so emptiness = zero logged hours across the week.
    isEmpty: (s) => !s?.effortSummary?.last7Days || (s.effortSummary.last7Days as any[]).every((d) => !d?.hours),
  },
  {
    id: 'my_allocation',
    label: 'My Allocation',
    description: 'Your project allocation this week',
    visibleWithPermissions: [],
    defaultForRoles: [...BASIC_ROLES],
    defaultForPositions: ['member', 'team_lead'],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'personal',
    priority: 20,
    isEmpty: (s) => (s?.myAllocation?.projects?.length ?? 0) === 0,
  },
  {
    id: 'upcoming_time_off',
    label: 'Upcoming Time Off',
    description: 'Your + teammates upcoming leave, WFH, and holidays (next 14 days)',
    visibleWithPermissions: [],
    defaultForRoles: [...BASIC_ROLES],
    defaultForPositions: ['member', 'team_lead', 'team_head'],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'personal',
    priority: 25,
  },
  {
    id: 'training_due',
    label: 'Training Due',
    description: 'Upcoming and overdue training assignments',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'personal',
    priority: 74,
  },

  // ── Team ──────────────────────────────────────────
  {
    id: 'pending_approvals',
    label: 'Pending Approvals',
    description: 'Leave and location requests awaiting your action',
    visibleWithPermissions: ['leave.approve'],
    defaultForRoles: ['leave_manager', ...MGMT_ROLES],
    defaultForPositions: ['team_head'],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'team',
    priority: 10,
    isEmpty: (s) => !s?.pendingApprovals || s.pendingApprovals.total === 0,
  },
  {
    id: 'team_availability',
    label: 'Team Today',
    description: 'Who is in office, WFH, or on leave today',
    visibleWithPermissions: ['leave.view_team'],
    defaultForRoles: ['leave_manager', ...MGMT_ROLES],
    defaultForPositions: ['member', 'team_lead', 'team_head'],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'team',
    priority: 15,
    isEmpty: (s) => !s?.teamAvailability,
  },
  {
    id: 'team_effort_snapshot',
    label: 'Team Effort',
    description: 'Team member hours logged this week',
    visibleWithPermissions: ['effort.view_team'],
    defaultForRoles: MGMT_ROLES,
    defaultForPositions: ['team_lead', 'team_head'],
    gridSpan: { default: 'col-span-12', lg: 'lg:col-span-8' },
    category: 'team',
    priority: 28,
  },
  {
    id: 'team_capacity',
    label: 'Team Capacity',
    description: 'Team allocation vs capacity overview',
    visibleWithPermissions: ['resources.view_team'],
    defaultForRoles: MGMT_ROLES,
    defaultForPositions: ['team_lead', 'team_head'],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'team',
    priority: 30,
    isEmpty: (s) => !s?.teamCapacity || (s.teamCapacity.overAllocated === 0 && s.teamCapacity.underAllocated === 0 && s.teamCapacity.optimal === 0 && s.teamCapacity.totalTeamMembers === 0),
  },
  {
    id: 'work_capacity',
    label: 'Work Capacity',
    description: 'Detailed capacity breakdown',
    visibleWithPermissions: ['resources.view_team'],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', lg: 'lg:col-span-8' },
    category: 'team',
    priority: 50,
  },

  // ── Management ────────────────────────────────────
  {
    id: 'project_health',
    label: 'Project Health',
    description: 'Project progress and risk indicators',
    visibleWithPermissions: ['analytics.view_team'],
    defaultForRoles: MGMT_ROLES,
    defaultForPositions: ['team_head'],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'management',
    priority: 40,
    isEmpty: (s) => !s?.projectHealth || s.projectHealth.length === 0,
  },
  {
    id: 'milestones',
    label: 'Milestones',
    description: 'Upcoming and overdue project milestones',
    visibleWithPermissions: ['analytics.view_team'],
    defaultForRoles: MGMT_ROLES,
    defaultForPositions: ['team_head'],
    gridSpan: { default: 'col-span-12', lg: 'lg:col-span-4' },
    category: 'management',
    priority: 42,
    isEmpty: (s) => !s?.upcomingMilestones || s.upcomingMilestones.length === 0,
  },
  {
    id: 'projects_table',
    label: 'Projects Table',
    description: 'Your active projects with status',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', lg: 'lg:col-span-8' },
    category: 'management',
    priority: 55,
  },

  // ── Content ───────────────────────────────────────
  {
    id: 'content_pulse',
    label: 'Content Pulse',
    description: 'News engagement, survey responses, events',
    visibleWithPermissions: ['content.manage_news', 'content.manage_surveys', 'content.manage_events'],
    defaultForRoles: ['content_manager', ...MGMT_ROLES],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'content',
    priority: 48,
  },

  // ── Analytics ─────────────────────────────────────
  {
    id: 'leave_overview',
    label: 'Leave Overview',
    description: 'Org-wide leave stats, absences, and alerts',
    visibleWithPermissions: ['leave.view_reports'],
    defaultForRoles: ['leave_manager', ...MGMT_ROLES],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', lg: 'lg:col-span-8' },
    category: 'analytics',
    priority: 38,
  },
  {
    id: 'analytics_snapshot',
    label: 'Analytics Snapshot',
    description: 'Org utilization trend and key metrics',
    visibleWithPermissions: ['analytics.view_org'],
    defaultForRoles: ['full_analytics', ...MGMT_ROLES],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'analytics',
    priority: 45,
  },

  // ── Social ────────────────────────────────────────
  {
    id: 'latest_news',
    label: 'Latest News',
    description: 'Recent company news and announcements',
    visibleWithPermissions: [],
    defaultForRoles: [...BASIC_ROLES, 'leave_manager', 'content_manager', 'full_analytics', ...MGMT_ROLES],
    defaultForPositions: ['member', 'team_lead', 'team_head'],
    gridSpan: { default: 'col-span-12', lg: 'lg:col-span-8' },
    category: 'social',
    priority: 18,
    pinned: true,
  },
  {
    id: 'quick_links',
    label: 'Quick Links',
    description: 'Useful bookmarks and shortcuts',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'social',
    priority: 70,
  },
  {
    id: 'upcoming_events',
    label: 'Upcoming Events',
    description: 'Events and holidays coming up',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'social',
    priority: 72,
  },
  {
    id: 'training_modules',
    label: 'Training Modules',
    description: 'Browse available training content',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', lg: 'lg:col-span-8' },
    category: 'social',
    priority: 76,
  },
  {
    id: 'team_birthdays',
    label: 'Team Birthdays',
    description: 'Upcoming team member birthdays',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'social',
    priority: 80,
  },
  {
    id: 'knowledge_highlights',
    label: 'Knowledge Highlights',
    description: 'Recent knowledge base articles',
    visibleWithPermissions: [],
    defaultForRoles: [],
    defaultForPositions: [],
    gridSpan: { default: 'col-span-12', sm: 'sm:col-span-6', lg: 'lg:col-span-4' },
    category: 'social',
    priority: 85,
  },
];

// ─── Helpers ─────────────────────────────────────────────

/** Get a widget definition by ID */
export function getWidgetDef(id: string): WidgetDef | undefined {
  return WIDGET_REGISTRY.find(w => w.id === id);
}

/** Get all widget IDs */
export function getAllWidgetIds(): string[] {
  return WIDGET_REGISTRY.map(w => w.id);
}

/** Build grid class string from a widget's gridSpan config */
export function getGridClasses(def: WidgetDef): string {
  const parts = [def.gridSpan.default];
  if (def.gridSpan.sm) parts.push(def.gridSpan.sm);
  if (def.gridSpan.lg) parts.push(def.gridSpan.lg);
  return parts.join(' ');
}

/**
 * Determine whether a widget currently has no meaningful data.
 * - Summary-driven widgets use their registry `isEmpty` predicate (only once the
 *   summary has loaded — while it's undefined we treat them as non-empty so they
 *   don't flicker to the bottom during initial load).
 * - Self-fetching widgets fall back to what they reported via `useReportTileEmpty`.
 * Pinned widgets are never considered empty (they hold their top-zone slot).
 */
export function isWidgetEmpty(
  def: WidgetDef,
  summary: any,
  tasks: any[],
  reportedEmpty: Record<string, boolean>,
): boolean {
  if (def.pinned) return false;
  if (def.isEmpty) {
    if (!summary) return false;
    return def.isEmpty(summary, tasks);
  }
  return reportedEmpty[def.id] ?? false;
}

/**
 * Order widget IDs for the grid: populated widgets first (in their existing
 * priority order), data-less widgets demoted to the bottom — so the top of the
 * dashboard is always useful and no empty cards leave gaps up top.
 * The incoming `orderedIds` is assumed already priority-sorted.
 */
export function partitionByEmptiness(
  orderedIds: string[],
  summary: any,
  tasks: any[],
  reportedEmpty: Record<string, boolean>,
): { id: string; empty: boolean }[] {
  const decorated = orderedIds.map((id) => {
    const def = WIDGET_REGISTRY.find((w) => w.id === id);
    const empty = def ? isWidgetEmpty(def, summary, tasks, reportedEmpty) : false;
    return { id, empty };
  });
  // Stable partition: keep priority order within each group.
  return [
    ...decorated.filter((d) => !d.empty),
    ...decorated.filter((d) => d.empty),
  ];
}

/** Category labels for the customize sheet grouping */
export const CATEGORY_LABELS: Record<WidgetCategory, string> = {
  personal: 'Personal',
  team: 'Team',
  management: 'Management',
  content: 'Content',
  analytics: 'Analytics',
  social: 'Social & News',
};

/** Defaults version — bump when changing default widget sets to trigger migration. */
export const DASHBOARD_DEFAULTS_VERSION = 2;
